import React, { useEffect } from "react";

import {
  DataGridPro,
  gridClasses,
  GridToolbarColumnsButton,
  GridToolbarContainer,
  GridToolbarDensitySelector,
  GridToolbarExport,
  GridToolbarFilterButton,
  GridToolbarQuickFilter,
  useGridApiRef,
} from "@mui/x-data-grid-pro";
import {
  styled,
  alpha,
  Radio,
  FormControl,
  FormControlLabel,
  FormLabel,
  RadioGroup,
  InputLabel,
  MenuItem,
  Select,
  SelectChangeEvent,
  CircularProgress,
  Box,
} from "@mui/material";
import { framing_columns, framing_columns_mobile } from "./utils/ColumnDefs";

const ODD_OPACITY = 0.2;

const StripedDataGrid = styled(DataGridPro)(({ theme }) => ({
  [`& .${gridClasses.row}.even`]: {
    backgroundColor: theme.palette.grey[200],
    "&:hover": {
      backgroundColor: alpha(theme.palette.primary.main, ODD_OPACITY),
      "@media (hover: none)": { backgroundColor: "transparent" },
    },
    "&.Mui-selected": {
      backgroundColor: alpha(
        theme.palette.primary.main,
        ODD_OPACITY + theme.palette.action.selectedOpacity
      ),
      "&:hover": {
        backgroundColor: alpha(
          theme.palette.primary.main,
          ODD_OPACITY +
            theme.palette.action.selectedOpacity +
            theme.palette.action.hoverOpacity
        ),
        "@media (hover: none)": {
          backgroundColor: alpha(
            theme.palette.primary.main,
            ODD_OPACITY + theme.palette.action.selectedOpacity
          ),
        },
      },
    },
  },
  [`& .${gridClasses.row}.odd`]: {
    backgroundColor: "white",
    "&:hover": {
      backgroundColor: alpha(theme.palette.primary.main, ODD_OPACITY),
      "@media (hover: none)": { backgroundColor: "transparent" },
    },
    "&.Mui-selected": {
      backgroundColor: alpha(
        theme.palette.primary.main,
        ODD_OPACITY + theme.palette.action.selectedOpacity
      ),
      "&:hover": {
        backgroundColor: alpha(
          theme.palette.primary.main,
          ODD_OPACITY +
            theme.palette.action.selectedOpacity +
            theme.palette.action.hoverOpacity
        ),
        "@media (hover: none)": {
          backgroundColor: alpha(
            theme.palette.primary.main,
            ODD_OPACITY + theme.palette.action.selectedOpacity
          ),
        },
      },
    },
  },
}));

export default function FramingBoard(props: any) {
  const apiRef = useGridApiRef();

  const [level, setLevel] = React.useState("AAA");
  const [season, setSeason] = React.useState("2026");
  const [minOpps, setMinOpps] = React.useState("25");
  const [framingData, setFramingData] = React.useState<Array<any>>([]);
  const [loading, setLoading] = React.useState(false);

  const columns = props.isDesktop ? framing_columns : framing_columns_mobile;

  const handleLevel = (event: React.ChangeEvent<HTMLInputElement>) => {
    setLevel(event.target.value);
  };

  const handleSeason = (event: SelectChangeEvent) => {
    setSeason(event.target.value as string);
  };

  const handleMinOpps = (event: SelectChangeEvent) => {
    setMinOpps(event.target.value as string);
  };

  function CustomToolbar() {
    return (
      <GridToolbarContainer sx={{ backgroundColor: "#FFFFFF" }}>
        <GridToolbarColumnsButton />
        <GridToolbarFilterButton />
        <GridToolbarDensitySelector />
        <GridToolbarExport />
        <GridToolbarQuickFilter
          quickFilterParser={(searchInput) =>
            searchInput.split(",").map((value) => value.trim())
          }
          sx={{ marginLeft: "auto", width: props.isDesktop ? 500 : 250 }}
        />
      </GridToolbarContainer>
    );
  }

  useEffect(() => {
    const base = props.prod ? "https://oriolebird.pythonanywhere.com" : "";
    setLoading(true);
    fetch(
      `${base}/framing-leaderboard/${season}/?level=${level}&min_opps=${minOpps}`,
      {
        headers: {
          Authorization: `Bearer ${localStorage.getItem("ps_token") ?? ""}`,
        },
      }
    )
      .then((res) => res.json())
      .then((data) => {
        setFramingData(data.data ?? []);
        setLoading(false);
      })
      .catch(() => setLoading(false));
  }, [level, season, minOpps, props.prod]);

  return (
    <div style={{ width: "100%" }}>
      <title>Prospect Savant – Catcher Framing</title>
      <meta name="description" content="MiLB catcher framing leaderboard — framing runs saved and rate stats." />

      <div
        style={{
          marginTop: "10px",
          marginBottom: "-20px",
          marginLeft: "30px",
          verticalAlign: "baseline",
        }}
      >
        <FormControl>
          <FormLabel id="framing-level-label">Level</FormLabel>
          <RadioGroup row aria-labelledby="framing-level-label" name="framing-level-group">
            {["A", "AAA"].map((lvl) => (
              <FormControlLabel
                key={lvl}
                value={lvl}
                control={
                  <Radio
                    checked={level === lvl}
                    onChange={handleLevel}
                    value={lvl}
                    name="framing-radio"
                    inputProps={{ "aria-label": lvl }}
                  />
                }
                label={lvl}
              />
            ))}
          </RadioGroup>
        </FormControl>

        <FormControl>
          <InputLabel id="framing-season-label">Season</InputLabel>
          <Select
            value={season}
            label="Season"
            onChange={handleSeason}
            defaultValue="2026"
          >
            <MenuItem value={2026}>2026</MenuItem>
            <MenuItem value={2025}>2025</MenuItem>
            <MenuItem value={2024}>2024</MenuItem>
            <MenuItem value={2023}>2023</MenuItem>
          </Select>
        </FormControl>

        <FormControl style={{ marginLeft: "10px" }}>
          <InputLabel id="framing-minopps-label">Min Opps</InputLabel>
          <Select
            value={minOpps}
            label="Min Opps"
            onChange={handleMinOpps}
            defaultValue="25"
            sx={{ minWidth: "110px" }}
          >
            <MenuItem value={0}>0</MenuItem>
            <MenuItem value={25}>25</MenuItem>
            <MenuItem value={50}>50</MenuItem>
            <MenuItem value={100}>100</MenuItem>
            <MenuItem value={200}>200</MenuItem>
          </Select>
        </FormControl>
      </div>

      <div>
        {loading ? (
          <Box sx={{ display: "flex", justifyContent: "center", mt: 10 }}>
            <CircularProgress />
          </Box>
        ) : (
          <StripedDataGrid
            apiRef={apiRef}
            rows={framingData}
            columns={columns}
            getRowId={(row) => row.fielder_2}
            getRowClassName={(params) =>
              params.indexRelativeToCurrentPage % 2 === 0 ? "even" : "odd"
            }
            slots={{ toolbar: CustomToolbar }}
            initialState={{
              sorting: { sortModel: [{ field: "framing_runs_p", sort: "asc" }] },
              pagination: { paginationModel: { pageSize: 100 } },
              density: "compact",
            }}
            pageSizeOptions={[25, 50, 100]}
            sx={{
              height: "80vh",
              width: props.isDesktop ? "99%" : "100%",
              marginTop: "20px",
              marginLeft: "auto",
              marginRight: "auto",
              "& .super-app": { padding: 0 },
              "& .MuiDataGrid-cell": { fontSize: props.isDesktop ? "0.875rem" : "0.75rem" },
            }}
          />
        )}
      </div>

      <Box
        sx={{
          maxWidth: props.isDesktop ? "60%" : "95%",
          mx: "auto",
          mt: 3,
          mb: 4,
          px: 2,
          py: 1.5,
          backgroundColor: "#f9f9f9",
          borderLeft: "4px solid #888",
          fontSize: "0.82rem",
          color: "#444",
          lineHeight: 1.7,
        }}
      >
        <strong>Methodology:</strong> An XGBoost classifier is trained on MLB pitch data to estimate the probability that any taken pitch is called a strike, given plate location, count, batter/pitcher handedness, and velocity. Only borderline miscalls are scored — in-zone pitches called ball (Statcast zones 1–9) and out-of-zone pitches called strike (zones 11–14). For each borderline pitch, framing value equals the difference between the expected run value (based on the model's strike probability) and the actual run value of the call received. Totals are summed across all scored pitches to produce <strong>Framing Runs</strong>; the rate stat <strong>FR/1000</strong> normalizes by miscall volume. <strong>Stolen CS</strong> = called strikes where P(strike) &lt; 0.5. <strong>Missed CS</strong> = called balls where P(strike) &gt; 0.5. <strong>Miscalls</strong> = total borderline pitches scored.
      </Box>
    </div>
  );
}
